import React from 'react';
import { Helmet } from 'react-helmet';
import Hero from '../components/Hero';
import ScrollToTopButton from '../components/ScrollToTopButton';
import ROICalculator from '../components/ROICalculator';
import ProblemSection from '../components/ProblemSection';
import SolutionSection from '../components/SolutionSection';
import SpecialtiesSection from '../components/SpecialtiesSection';
import PatientExperienceCarousel from '../components/PatientExperienceCarousel';
import SecurityComplianceSection from '../components/SecurityComplianceSection';
import RoiImpactSection from '../components/RoiImpactSection';
import AdvantageSection from '../components/AdvantageSection';
import ConsultationCTA from '../components/ConsultationCTA';

const Home = () => {
  // Current date for dynamic schema
  const currentDate = new Date().toISOString();
  
  // ✅ OPTIMIZED Structured Data for NEW LOW-COMPETITION KEYWORDS
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "MDeRCM - Medical Billing Outsourcing for Small Practices & Independent Physicians",
    "alternateName": "Revenue Cycle Management for Small Practices",
    "description": "Specialized medical billing outsourcing for small practices, independent physicians, and multispecialty clinics. Expert denial management services to reduce claim denials in healthcare billing with proven results.",
    "url": "https://mdercm.com",
    "logo": "https://mdercm.com/logo192.png",
    "image": "https://mdercm.com/hero-bg.jpg",
    "telephone": "+1-510-356-6069",
    "email": "info@mdercm.com",
    "priceRange": "$$",
    "foundingDate": "2018",
    "slogan": "Expert Medical Billing Outsourcing for Small Practices & Independent Physicians",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "525 Route 73 North, Suite 104",
      "addressLocality": "Marlton",
      "addressRegion": "NJ",
      "postalCode": "08053",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "39.8917",
      "longitude": "-74.9195"
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "United States"
      },
      {
        "@type": "State",
        "name": "All 50 US States"
      }
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-510-356-6069",
      "contactType": "Customer Service",
      "areaServed": ["US", "USA"],
      "availableLanguage": "English",
      "hoursAvailable": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      }
    },
    "sameAs": [
      "https://www.linkedin.com/company/mdercm",
      "https://twitter.com/mdercm",
      "https://www.facebook.com/mdercm"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "makesOffer": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Medical Billing Outsourcing for Small Practices",
          "description": "Comprehensive medical billing and coding services designed specifically for small practices and independent physicians"
        },
        "price": "Contact for pricing",
        "priceCurrency": "USD"
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Denial Management Services for Medical Clinics",
          "description": "Expert denial management to reduce claim denials in healthcare billing"
        },
        "price": "Contact for pricing",
        "priceCurrency": "USD"
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Revenue Cycle Management for Independent Physicians",
          "description": "Specialized RCM solutions for independent physicians and solo practitioners"
        },
        "price": "Contact for pricing",
        "priceCurrency": "USD"
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Revenue Cycle Management for Multispecialty Practices",
          "description": "Tailored RCM services for multispecialty medical practices"
        },
        "price": "Contact for pricing",
        "priceCurrency": "USD"
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://mdercm.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Medical Billing Outsourcing for Small Practices",
        "item": "https://mdercm.com"
      }
    ]
  };

  // ✅ UPDATED FAQ Schema with NEW LOW-COMPETITION KEYWORDS
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is medical billing outsourcing for small practices?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Medical billing outsourcing for small practices is a cost-effective solution where specialized billing companies handle all billing operations, including claims submission, payment posting, and denial management. This allows small practices to reduce overhead costs by 30-50% while improving collection rates and allowing physicians to focus on patient care."
        }
      },
      {
        "@type": "Question",
        "name": "How can I reduce claim denials in healthcare billing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "To reduce claim denials in healthcare billing, implement AI-powered claim validation, ensure accurate coding, verify insurance eligibility before appointments, submit clean claims within 24 hours, and use expert denial management services. Professional medical billing outsourcing can reduce denial rates by 35-45% through systematic error prevention and quick resubmission processes."
        }
      },
      {
        "@type": "Question",
        "name": "What are the benefits of medical billing outsourcing for clinics?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Benefits of medical billing outsourcing for clinics include: 30-50% reduction in operational costs, 98% claim accuracy rates, faster reimbursement cycles (15-20 days vs 45-60 days), access to expert billing specialists, reduced denial rates, elimination of billing staff overhead, real-time revenue analytics, and HIPAA-compliant processes. Small practices and multispecialty clinics see immediate ROI."
        }
      },
      {
        "@type": "Question",
        "name": "What is revenue cycle management for independent physicians?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Revenue cycle management for independent physicians is a comprehensive service that handles the entire billing process from patient registration to final payment collection. It includes insurance verification, claims submission, denial management, payment posting, and accounts receivable follow-up. Independent physicians benefit from reduced administrative burden, improved cash flow, and lower overhead costs."
        }
      },
      {
        "@type": "Question",
        "name": "How does denial management services for medical clinics work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Denial management services for medical clinics identify, analyze, and resolve claim denials through systematic processes. Expert teams review denied claims, determine root causes, correct errors, resubmit claims quickly, and implement prevention strategies. This service reduces denial rates by 35-45% and recovers 60-70% of denied revenue that would otherwise be lost."
        }
      },
      {
        "@type": "Question",
        "name": "What is revenue cycle management for multispecialty practices?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Revenue cycle management for multispecialty practices is specialized service designed to handle the complex billing needs of practices with multiple specialties. It manages different coding requirements, varied payer contracts, specialty-specific compliance rules, and coordinated care documentation. This ensures maximum reimbursement across all specialties while maintaining compliance and reducing administrative complexity."
        }
      }
    ]
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "MDeRCM",
    "alternateName": "Medical Billing Outsourcing for Small Practices",
    "legalName": "MDeRCM Healthcare Revenue Cycle Management Services",
    "url": "https://mdercm.com",
    "logo": "https://mdercm.com/logo192.png",
    "foundingDate": "2018",
    "founders": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Sales",
      "telephone": "+1-510-356-6069",
      "email": "info@mdercm.com",
      "availableLanguage": "English"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "525 Route 73 North, Suite 104",
      "addressLocality": "Marlton",
      "addressRegion": "NJ",
      "postalCode": "08053",
      "addressCountry": "US"
    }
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "MDeRCM - Medical Billing Outsourcing for Small Practices",
    "url": "https://mdercm.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://mdercm.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      <Helmet>
        {/* ✅ OPTIMIZED Title & Description for NEW KEYWORDS */}
        <title>Medical Billing Outsourcing for Small Practices | MDeRCM</title>
        <meta 
          name="description" 
          content="Expert medical billing outsourcing for small practices & independent physicians. Reduce claim denials 35-45%, cut costs 30-50%. Free trial available." 
        />
        
        {/* ✅ NEW Keywords Meta Tag - LOW COMPETITION FOCUS */}
        <meta 
          name="keywords" 
          content="medical billing outsourcing for small practices, reduce claim denials in healthcare billing, revenue cycle management for independent physicians, denial management services for medical clinics, revenue cycle management for multispecialty practices, benefits of medical billing outsourcing for clinics, small practice medical billing, independent physician billing services, multispecialty RCM solutions, healthcare billing denial reduction" 
        />
        
        {/* Open Graph / Facebook - UPDATED */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mdercm.com/" />
        <meta property="og:title" content="Medical Billing Outsourcing for Small Practices | Independent Physicians RCM" />
        <meta property="og:description" content="Expert medical billing outsourcing for small practices. Reduce claim denials by 35-45% with specialized denial management services for clinics." />
        <meta property="og:image" content="https://mdercm.com/hero-bg.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="MDeRCM" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter - UPDATED */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://mdercm.com/" />
        <meta property="twitter:title" content="Medical Billing Outsourcing for Small Practices | MDeRCM" />
        <meta property="twitter:description" content="Reduce claim denials in healthcare billing by 35-45%. Expert RCM for independent physicians & multispecialty practices." />
        <meta property="twitter:image" content="https://mdercm.com/hero-bg.jpg" />
        <meta name="twitter:image:alt" content="Medical Billing Outsourcing for Small Practices" />

        {/* ✅ SEO Tags */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="author" content="MDeRCM" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* ✅ Geo Tags for Local SEO */}
        <meta name="geo.region" content="US" />
        <meta name="geo.placename" content="United States" />
        <meta name="geo.position" content="39.8917;-74.9195" />
        <meta name="ICBM" content="39.8917, -74.9195" />
        
        {/* ✅ Canonical URL */}
        <link rel="canonical" href="https://mdercm.com/" />
        
        {/* ✅ Alternate for mobile */}
        <link rel="alternate" media="only screen and (max-width: 640px)" href="https://mdercm.com/" />
        
        {/* ✅ Hreflang for International SEO */}
        <link rel="alternate" hrefLang="en-us" href="https://mdercm.com/" />
        <link rel="alternate" hrefLang="x-default" href="https://mdercm.com/" />
        
        {/* ✅ ALL Structured Data Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>

        {/* ✅ Preconnect for Performance */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        
        {/* ✅ DNS Prefetch for faster loading */}
        <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
        <link rel="dns-prefetch" href="https://www.google-analytics.com" />

        {/* ✅ Resource Hints */}
        <link rel="preload" as="image" href="/hero-bg.jpg" />
      </Helmet>

      {/* ✅ Main Content with Semantic HTML */}
      <main itemScope itemType="https://schema.org/WebPage" role="main">
        
        {/* Hero Section */}
        <article itemProp="mainEntity" itemScope itemType="https://schema.org/Service">
          <Hero />
        </article>
        
        {/* Patient Experience Section */}
        <section 
          aria-label="AI-Powered Medical Billing Services for Small Practices"
          itemScope 
          itemType="https://schema.org/Service"
        >
          <PatientExperienceCarousel />
        </section>
        
        {/* Problem Section */}
        <section 
          aria-label="Healthcare Billing Challenges for Small Practices"
          itemScope 
          itemType="https://schema.org/Article"
        >
          <ProblemSection />
        </section>
        
        {/* Solution Section */}
        <section 
          aria-label="Medical Billing Outsourcing Solutions"
          itemScope 
          itemType="https://schema.org/Service"
        >
          <SolutionSection />
        </section>
        
        {/* Specialties Section */}
        <section 
          aria-label="Medical Billing Specialties"
          itemScope 
          itemType="https://schema.org/ItemList"
        >
          <SpecialtiesSection />
        </section>
        
        {/* Security & Compliance */}
        <section 
          aria-label="HIPAA Compliance and Data Security"
          itemScope 
          itemType="https://schema.org/Article"
        >
          <SecurityComplianceSection />
        </section>
        
        {/* Advantages */}
        <section 
          aria-label="Benefits of Medical Billing Outsourcing"
          itemScope 
          itemType="https://schema.org/ItemList"
        >
          <AdvantageSection />
        </section>
        
        {/* ROI Impact */}
        <section 
          aria-label="ROI and Cost Reduction for Small Practices"
          itemScope 
          itemType="https://schema.org/Article"
        >
          <RoiImpactSection />
        </section>
        
        {/* ROI Calculator */}
        <section 
          id="roi-calculator" 
          aria-label="Calculate Your Medical Billing Savings"
        >
          <ROICalculator />
        </section>
        
       
        
        {/* Consultation CTA */}
        <section 
          aria-label="Free Consultation for Small Practices"
          itemScope 
          itemType="https://schema.org/ContactPage"
        >
          <ConsultationCTA />
        </section>
        
        <ScrollToTopButton />
      </main>
    </>
  );
};

export default Home;